import {Formik, FormikProps, FormikHelpers} from 'formik';
import {forEach, isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation, withTranslation, WithTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {userApi} from 'api';
import {
    Button, FormGroup, InputFormik, UnauthorizedLayoutContent, Form, notifier,
    NotRobotCaptcha, PasswordInputFormik, MfaVerifyEmail
} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {history} from 'context/history';
import {ValidationCode} from 'enums';
import {UnauthorizedLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {ApiError, AuthData, LoginData} from 'types';
import {apiUtils, userAuthUtils} from 'utils';

const PAGE_KEYS = TRANSLATION_KEYS.LOGIN_PAGE;
const FORM_KEYS = TRANSLATION_KEYS.LOGIN_PAGE.FORM;

const REFRESH_TOKEN_EXPIRED = TRANSLATION_KEYS.COMMON.MESSAGES.ERROR.REFRESH_TOKEN_EXPIRED;

const OVERRIDES = {
    email: {
        [ValidationCode.Invalid]: PAGE_KEYS.ERRORS.INVALID_CREDENTIALS,
        [ValidationCode.InvalidFormat]: PAGE_KEYS.ERRORS.FORMAT.EMAIL
    }
};

type FormFields = LoginData;

type FormTemplateProps = {
    validationErrors: ApiError;
    captchaRequired: boolean;
} & WithTranslation & FormikProps<FormFields>;

enum View {
    Login = 'login',
    MfaVerify = 'mfa-verify'
}

const FormTemplate = withTranslation()(({
    captchaRequired,
    isSubmitting,
    values,
    errors,
    validationErrors,
    setFieldValue,
    handleSubmit,
    setFieldError,
    t
}: FormTemplateProps) => {
    const disabled = useMemo(() =>
        isSubmitting || !isEmpty(errors) || !values.email || !values.password || (captchaRequired && !values.captchaToken),
    [isSubmitting, values, errors, captchaRequired]);

    const setCaptchaToken = useCallback((value: string) => {
        setFieldValue('captchaToken', value);
    }, [setFieldValue]);

    const onCaptchaExpired = useCallback(() => {
        setCaptchaToken(null);
    }, [setCaptchaToken]);

    useEffect(() => {
        const err = apiUtils.getFieldsValidationErrorMessages(validationErrors, t, [{field: 'email', overrides: OVERRIDES.email}]);
        forEach(err, (value, key) => value && setFieldError(key, value));
    }, [validationErrors, setFieldError, t]);

    return (
        <Form
            noValidate
            className='tw-mt-6'
            onSubmit={handleSubmit}
        >
            <FormGroup>
                <InputFormik
                    name='email'
                    autoFocus
                    required
                    type='email'
                    autoComplete='email'
                    label={t(FORM_KEYS.EMAIL)}
                />
            </FormGroup>
            <FormGroup>
                <PasswordInputFormik
                    required
                    name='password'
                    label={t(FORM_KEYS.PASSWORD)}
                />
            </FormGroup>

            {captchaRequired && !isSubmitting && !isEmpty(validationErrors) &&
                <div className='tw-flex tw-justify-center tw-mt-10'>
                    <NotRobotCaptcha
                        onChange={setCaptchaToken}
                        onExpired={onCaptchaExpired}
                    />
                </div>}

            <div className='tw-mt-12'>
                <Button
                    width='full'
                    height='full'
                    type='submit'
                    color='primary'
                    disabled={disabled}
                >
                    {t(FORM_KEYS.SIGN_IN)}
                </Button>
            </div>
        </Form>
    );
});

type LoginPageLocationState = {
    tokenExpired: boolean;
    previousPath: string;
}

type Props = {
    onSuccessLogin: () => void;
};

export const LoginPage = ({onSuccessLogin}: Props) => {
    const {t} = useTranslation();
    const location = useLocation<LoginPageLocationState>();
    const [errors, setErrors] = useState(null);
    const [view, setView] = useState(View.Login);
    const [captchaRequired, setCaptchaRequired] = useState(false);
    const [accessToken, setAccessToken] = useState('');
    const [initialValues, setInitialValues] = useState<FormFields>({
        email: '',
        password: '',
        captchaToken: null
    });

    const isTokenExpired = useMemo(() => location.state?.tokenExpired, [location]);
    const previousPath = useMemo(() => location.state?.previousPath, [location]);
    const redirectUrl = useMemo(() => previousPath && previousPath !== '/' ? previousPath : null, [previousPath]);

    useEffect(() => {
        if (isTokenExpired) {
            notifier.error({details: t(REFRESH_TOKEN_EXPIRED)});
        }
    // it should work only on mount
    // eslint-disable-next-line
    }, []);

    const handleSuccessLogin = useCallback((data: AuthData) => {
        userAuthUtils.setAuthData(data);
        onSuccessLogin();
        if (redirectUrl) {
            history.push(redirectUrl);
        }
    }, [onSuccessLogin, redirectUrl]);

    const handleSubmit = useCallback((values: FormFields, {setSubmitting}: FormikHelpers<FormFields>) => {
        setInitialValues(values);
        setErrors(null);
        setSubmitting(true);
        return userApi.login(values)
            .then((data: AuthData) => {
                setAccessToken(data.accessToken);
                const payload = userAuthUtils.parseJwtPayload(data.accessToken);
                if (payload.mfa) {
                    handleSuccessLogin(data);
                } else {
                    setView(View.MfaVerify);
                }
            })
            .catch((error) => {
                setInitialValues({...values, captchaToken: null});
                const data = apiUtils.getValidationErrors(error);
                if (data?.errors?.captchaToken) {
                    notifier.info({details: t(PAGE_KEYS.ERRORS.CAPTCHA_REQUIRED)});
                    setCaptchaRequired(true);
                } else if (data?.errors?.ip) {
                    notifier.error({
                        title: t(PAGE_KEYS.ERRORS.IP_WHITELIST),
                        ...(data.errors.ip.value && {details: data.errors.ip.value})
                    });
                } else if (!data?.errors?.password && !data?.errors?.email) {
                    notifier.error({details: t(PAGE_KEYS.ERRORS.UNEXPECTED)});
                }
                setErrors(error);
                setSubmitting(false);
            });
    }, [setErrors, handleSuccessLogin, t]);

    return (
        <UnauthorizedLayout icon='lock-closed'>
            <UnauthorizedLayoutContent
                title={t(FORM_KEYS.SIGN_IN)}
                {...(view === View.Login && {
                    link: {path: ROUTE_PATH.PUBLIC.PASSWORD.FORGOT, name: t(PAGE_KEYS.FORGOT_PASSWORD)}
                })}
            >
                {view === View.Login &&
                    <Formik
                        initialValues={initialValues}
                        validateOnChange
                        enableReinitialize
                        validateOnBlur={false}
                        onSubmit={handleSubmit}
                    >
                        {(props) => (
                            <FormTemplate {...props} validationErrors={errors} captchaRequired={captchaRequired} />
                        )}
                    </Formik>}
                {view === View.MfaVerify &&
                    <MfaVerifyEmail
                        accessToken={accessToken}
                        onCancel={() => setView(View.Login)}
                        onSuccess={handleSuccessLogin}
                    />}
            </UnauthorizedLayoutContent>
        </UnauthorizedLayout>
    );
};
